/**
 * Knowledge Base Service for handling communication with the knowledge base API
 * Provides methods for collecting data from web and files
 */
import api from './api';

const knowledgeBaseService = {
  /**
   * Collect data from a web URL
   * @param {string} url - The web page URL
   * @param {boolean} fullScan - Whether to crawl the entire domain
   * @param {number} crawlDepth - Maximum depth for crawling
   * @param {number} maxPages - Maximum number of pages to crawl
   * @returns {Promise} - Promise containing the API response
   */
  collectFromWeb: async (url, fullScan = false, crawlDepth = 2, maxPages = 10, force = false, onProgress = null) => {
    try {
      console.log('Collecting data from web:', { url, fullScan, crawlDepth, maxPages, force });

      // Collection runs as a background job on the server (a multi-page crawl can
      // take minutes). Start the job, then poll for the result so the request
      // never hits the browser HTTP timeout.
      const start = await api.post('/collect/web', {
        url,
        full_scan: fullScan,
        crawl_depth: crawlDepth,
        max_pages: maxPages,
        force
      });

      const jobId = start.data?.job_id;
      if (!jobId) {
        // Backwards-compat: an older server may still return the result directly.
        return start.data;
      }

      const POLL_MS = 2000;
      const MAX_WAIT_MS = 10 * 60 * 1000; // 10 minutes
      const t0 = Date.now();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        const status = await api.get(`/collect/web/status/${jobId}`);
        const data = status.data || {};
        if (data.status === 'done') {
          console.log('Web collection done:', data);
          return data;
        }
        if (data.status === 'error') {
          throw new Error(data.error || 'Web collection failed');
        }
        if (typeof onProgress === 'function') {
          onProgress(Math.round((Date.now() - t0) / 1000));
        }
        if (Date.now() - t0 > MAX_WAIT_MS) {
          throw new Error('Web collection timed out (still running on the server).');
        }
      }
    } catch (error) {
      console.error('Error collecting from web:', error);
      throw error;
    }
  },
  
  /**
   * Collect data from GitHub repositories by search query
   * @param {string} query - Search query (e.g. "water treatment python")
   * @param {number} maxRepos - Max repos to collect
   * @param {string} language - Optional language filter
   * @param {boolean} collectDocs - Also collect /docs files
   * @param {boolean} force - Re-collect already-harvested repos
   * @returns {Promise} - Promise containing the API response
   */
  collectFromGithub: async (query, maxRepos = 5, language = '', collectDocs = true, force = false) => {
    try {
      console.log('Collecting data from GitHub:', { query, maxRepos, language, collectDocs, force });

      const payload = {
        query,
        max_repos: maxRepos,
        collect_docs: collectDocs,
        force
      };
      if (language) payload.language = language;

      const response = await api.post('/collect/github', payload);

      console.log('GitHub collection response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error collecting from GitHub:', error);
      throw error;
    }
  },

  /**
   * Collect data from an uploaded file
   * @param {File} file - The file to upload
   * @returns {Promise} - Promise containing the API response
   */
  collectFromFile: async (file) => {
    try {
      console.log('Collecting data from file:', file.name);
      
      const formData = new FormData();
      formData.append('uploaded_file', file);
      
      const response = await api.post('/collect/file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('File collection response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error collecting from file:', error);
      throw error;
    }
  },

  /**
   * Process raw data entries
   * @returns {Promise} - Promise containing the API response
   */
  processRawData: async () => {
    try {
      console.log('Processing raw data');
      
      const response = await api.post('/cleanup/process-raw');
      
      console.log('Raw data processing response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error processing raw data:', error);
      throw error;
    }
  },

  /**
   * List raw metadata entries
   * @returns {Promise} - Promise containing the API response with metadata entries
   */
  listRawMetadata: async () => {
    try {
      console.log('Listing raw metadata');
      
      const response = await api.get('/metadata/raw');
      
      console.log('Raw metadata response:', response.data);
      // Return with entries key for consistency with frontend component
      return { entries: response.data.raw_entries || [] };
    } catch (error) {
      console.error('Error listing raw metadata:', error);
      throw error;
    }
  },

  /**
   * Run embedding process
   * @param {string} mode - Mode: "pipeline" or "chunks"
   * @param {string} chunkDir - Directory for chunk files (chunks mode only)
   * @param {string} tableName - Embeddings table name
   * @returns {Promise} - Promise containing the API response
   */
  runEmbedding: async (mode = 'pipeline', chunkDir = null, tableName = 'embeddings', onProgress = null) => {
    try {
      console.log('Running embedding process:', { mode, chunkDir, tableName });

      // Embedding runs as a background job on the server (one vector API call per
      // chunk can take minutes). Start it, then poll so we never hit the browser
      // HTTP timeout.
      const start = await api.post('/embedding/run', {
        mode,
        chunk_dir: chunkDir,
        table_name: tableName
      });

      const jobId = start.data?.job_id;
      if (!jobId) {
        // Backwards-compat: an older server may return the result directly.
        return start.data;
      }

      const POLL_MS = 2000;
      const MAX_WAIT_MS = 15 * 60 * 1000; // 15 minutes
      const t0 = Date.now();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        const status = await api.get(`/embedding/run/status/${jobId}`);
        const data = status.data || {};
        if (data.job_status === 'done') {
          console.log('Embedding done:', data.result);
          return data.result;
        }
        if (data.job_status === 'error') {
          throw new Error(data.error || 'Embedding failed');
        }
        if (typeof onProgress === 'function') {
          onProgress(Math.round((Date.now() - t0) / 1000));
        }
        if (Date.now() - t0 > MAX_WAIT_MS) {
          throw new Error('Embedding timed out (still running on the server).');
        }
      }
    } catch (error) {
      console.error('Error running embedding process:', error);
      throw error;
    }
  }
};

export default knowledgeBaseService;

